import { Component, OnInit, Input, HostBinding, ViewChild, ElementRef, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { IUProperty } from 'src/app/shared/interface/umbraco-property';
import { IPictureData } from '../../shared/components/picture/picture.component';
import { config } from '../../app.config';
import get from 'lodash.get';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { DomSanitizer } from '@angular/platform-browser';
import { MqService } from '../../service/mq.service';
import { IVideoPickerVideoData } from 'src/app/shared/interface/video-picker-data';
import { applyTitleSettings } from '../../shared/utils/title-style-settings-utils';
import { ITitleStyleSettings } from '../../shared/interface/title-style-settings';
import { applyItemSettings } from '../../shared/utils/item-settings-utils';
import { IItemSettings } from '../../shared/interface/item-settings';

interface IAnimationCarouselPanelData {
  title: IUProperty<string>;
  items: IUProperty<Array<ItemData>>;
  backgroundImage?: IUProperty<IPictureData>;
  topWave?: IUProperty<boolean>;
  bottomWave?: IUProperty<boolean>;
  itemsSelectedColor: IUProperty<string>;

  itemSettingsComposition?: IItemSettings;
  titleStyleSettings?: ITitleStyleSettings;
}

export interface ItemData {
  animation: IUProperty<IVideoPickerVideoData>;
  header: IUProperty<string>;
  headerColor: IUProperty<string>;
  backgroundColor: IUProperty<string>;
  description: IUProperty<string>;
  descriptionColor: IUProperty<string>;
  icon: IUProperty<IPictureData>;
  state?: string;
  dropdown?: string;
}

export interface IVideoViewModel {
  video: object;
}

export enum AnimationState {
  active = 'active',
  inactive = 'inactive'
}

enum DropdownState {
  opened = 'opened',
  closed = 'closed'
}

@Component({
  selector: 'app-animation-carousel-panel',
  templateUrl: './animation-carousel-panel.component.html',
  styleUrls: ['./animation-carousel-panel.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('dropdown', [
      state('opened', style({
        height: '*',
        opacity: '1',
      })),
      state('closed', style({
        opacity: '0',
        height: '0',
        display: 'none',
      })),
      transition('* => *', [animate('.3s ease-in-out')])
    ]),
    trigger('arrow', [
      state('active', style({
        opacity: '1',
        fill: 'var(--carousel-items-selected-color, #6F8D6F)',
        stroke: 'var(--carousel-items-selected-color, #6F8D6F)'
      })),
      state('inactive', style({
        opacity: '0',
        fill: 'transparent',
        stroke: 'transparent'
      })),
      transition('* => *', [animate('.1s ease-in-out')])
    ])
  ]
})
export class AnimationCarouselPanelComponent implements OnInit {
  @Input() data: IAnimationCarouselPanelData;
  @ViewChild('panelEl', { static: false }) panelElRef: ElementRef<HTMLElement>;
  @ViewChild('titleEl', { static: false }) titleEl: ElementRef<HTMLElement>;
  @ViewChild('itemsContainer', { static: false }) itemsContainerEl: ElementRef<HTMLElement>;
  @HostListener('window:scroll', []) onWindowScroll() {
    this.scrollPosition = window.pageYOffset;
    if ((this.scrollPosition + window.outerHeight) > this.panelElRef.nativeElement.offsetTop) {
      this.loadMedia = true;
    }
  }

  animations: ItemData[] = [];
  items: any[] = [];
  isMobile: boolean;
  readonly desktopMediaQuery = config.desktopMediaQuery || '(min-width: 768px)';
  currentAnimation: number;
  dropdownOpened: string = DropdownState.closed;
  animationStates = AnimationState;
  dropdownStates = DropdownState;
  isClicked: boolean;
  scrollPosition: number = null;
  loadMedia: boolean = false;

  constructor(
    private _sanitizer: DomSanitizer,
    private mq: MqService,
    private hostRef: ElementRef
  ) { }

  ngOnInit() {
    this.animations = (this.data && this.data.items && this.data.items.value) || [];
    this.animations.forEach(item => {
      item.state = AnimationState.inactive;
      item.dropdown = DropdownState.closed;
    });

    this.mq.mobileDesktop(this.changeToMobile.bind(this), this.changeToDesktop.bind(this));
    this.animations = this.data.items.value.map(item => ({
      ...item,
      videoData: this.isMobile ? this.mobile(item) : this.desktop(item)
    })
    );

    this.animations[0].state = AnimationState.active;
    this.animations[0].dropdown = DropdownState.opened;
    this.currentAnimation = 0;
    this.isClicked = false;

    this.scrollPosition = window.pageYOffset;
  }

  ngAfterViewInit(): void {
    if ((this.scrollPosition + window.outerHeight) > this.panelElRef.nativeElement.offsetTop) {
      this.loadMedia = true;
    }
    if (this.panelElRef && this.panelElRef.nativeElement && this.data && this.data.backgroundImage && this.data.backgroundImage.value) {
      this.panelElRef.nativeElement.style.backgroundImage = 'url('+ this.data.backgroundImage.value.src +')';
      this.panelElRef.nativeElement.style.backgroundSize = 'cover';
      this.panelElRef.nativeElement.style.backgroundRepeat = 'no-repeat';
    }

    this.setCustomThemeProperties(this.data);
    applyTitleSettings(this.data, this.titleEl);
  }

  changeToMobile() {
    this.isMobile = true;
  }

  changeToDesktop() {
    this.isMobile = false;
  }

  mobile(item) {
    return this.prepareVm(get(item, 'animation.value.mobile'));
  }
  desktop(item) {
    return this.prepareVm(get(item, 'animation.value.desktop'));
  }

  open() {
    this.items[this.currentAnimation].isShow = true;
  }
  close() {
    this.items[this.currentAnimation].isShow = false;
  }

  private prepareVm(data: IVideoPickerVideoData): IVideoViewModel {
    if (!data) return null;

    return {
      video: {
        url: data.url,
        type: data.type,
      }
    };
  }

  toggleAnimationState(item: any, index: number) {
    item.state = AnimationState.active;
    this.animations[this.currentAnimation].state = AnimationState.inactive;

    this.currentAnimation = index;
  }

  cardClick(item: any, index: number) {
    if (item.state === AnimationState.active) return;

    if (!this.isClicked) this.isClicked = true;

    this.toggleAnimationState(item, index);
  }

  dropdownClick(item: any, index: number) {
    if (item.state === AnimationState.active) return;

    if (!this.isClicked) this.isClicked = true;

    if (this.dropdownOpened = DropdownState.opened) {
      this.toggleAnimationState(item, index);
    }
  }

  nextAnimation() {
    if (this.isClicked === true) return;

    if (this.currentAnimation === this.arrayLength() - 1) {
      this.animations[0].state = AnimationState.active;
      this.animations[this.currentAnimation].state = AnimationState.inactive;

      this.animations[0].dropdown = DropdownState.opened;
      this.animations[this.currentAnimation].dropdown = DropdownState.closed;

      this.currentAnimation = 0;
    } else {
      this.animations[this.currentAnimation + 1].state = AnimationState.active;
      this.animations[this.currentAnimation].state = AnimationState.inactive;

      this.animations[this.currentAnimation + 1].dropdown = DropdownState.opened;
      this.animations[this.currentAnimation].dropdown = DropdownState.closed;

      this.currentAnimation = this.currentAnimation + 1;
    }
  }

  arrayLength(): number {
    return this.animations.length;
  }

  toggleDropdownState() {
    if (this.dropdownOpened === DropdownState.closed) {
      this.animations.forEach(item => {
        item.dropdown = DropdownState.opened;
      });
      this.dropdownOpened = DropdownState.opened;
    } else {
      this.animations.forEach(item => {
        item.dropdown = DropdownState.closed;
      });
      this.dropdownOpened = DropdownState.closed;

      this.animations[this.currentAnimation].dropdown = DropdownState.opened;
    }
  }

  assembleHTMLItem(item: any) {
    return this._sanitizer.bypassSecurityTrustHtml(item);
  }

  private setCustomThemeProperties(d: IAnimationCarouselPanelData): void {
    if (d.itemsSelectedColor && d.itemsSelectedColor.value) {
      this.hostRef.nativeElement.style.setProperty(
        "--carousel-items-selected-color",
        d.itemsSelectedColor.value
      );
    }
    applyItemSettings(this.data, this.hostRef);
  }
}
